import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown, type MarkdownStorage } from 'tiptap-markdown';

function getMarkdown(ed: Editor): string {
  const storage = ed.storage as unknown as { markdown: MarkdownStorage };
  return storage.markdown.getMarkdown();
}
import { Toast } from './Toast';
import type { WysiwygSaveState } from './saveState';

export type { WysiwygSaveState };

export interface WysiwygEditorProps {
  value: string;
  onChange: (md: string) => void;
  onSave: () => void;
  saveState: WysiwygSaveState;
  placeholder?: string;
  readonly?: boolean;
  className?: string;
  /** Test hook — appended to data-testid prefixes when set. */
  dataTestId?: string;
  /**
   * Hide the inline toolbar Save button when the host page already
   * surfaces a Save affordance (e.g. work-item-detail's sticky save bar).
   * The Cmd/Ctrl+S keyboard shortcut and the conflict banner continue to
   * work; only the in-toolbar button is suppressed.
   */
  hideInlineSave?: boolean;
  /**
   * Render a visible formatting toolbar (Bold / Italic / headings / lists /
   * code / quote / link / divider). When false (default), the editor still
   * exposes slash-commands; this prop adds an explicit visual toolbar for
   * users who do not know the shortcuts.
   */
  showFormattingToolbar?: boolean;
  /**
   * Strip the editor's own border, radius, and background so it sits flush
   * inside a host `Card`. The editor's content area and toolbar still render
   * normally; only the outer chrome is suppressed.
   */
  flush?: boolean;
}

const CONFLICT_COPY =
  'Someone else saved since you opened this. Reload to see their changes — your unsaved text is preserved below.';

function saveButtonLabel(state: WysiwygSaveState): string {
  switch (state) {
    case 'saving':
      return 'Saving…';
    case 'saved':
      return 'Saved';
    case 'conflict':
      return 'Save';
    case 'failed':
      return 'Retry save';
    default:
      return 'Save';
  }
}

// --- Slash commands -------------------------------------------------------
// Implemented inline (rather than as a heavyweight Tiptap Suggestion plugin)
// so the primitive stays self-contained. `/` at the start of an empty
// paragraph opens the command picker.
interface MenuState {
  /** Doc position of the trigger character (`/` or `@`). */
  anchorPos: number;
  /** Viewport coordinates the popover should anchor to. */
  x: number;
  y: number;
  /** Characters typed after the trigger — used to filter the list. */
  query: string;
  /** Currently highlighted item in the (filtered) list. */
  index: number;
}

interface SlashCommand {
  id: string;
  label: string;
  run: (ed: Editor) => void;
}

const SLASH_COMMANDS: SlashCommand[] = [
  {
    id: 'h1',
    label: 'Heading 1',
    run: (ed) => ed.chain().focus().setNode('heading', { level: 1 }).run()
  },
  {
    id: 'h2',
    label: 'Heading 2',
    run: (ed) => ed.chain().focus().setNode('heading', { level: 2 }).run()
  },
  {
    id: 'h3',
    label: 'Heading 3',
    run: (ed) => ed.chain().focus().setNode('heading', { level: 3 }).run()
  },
  {
    id: 'bullet',
    label: 'Bullet list',
    run: (ed) => ed.chain().focus().toggleBulletList().run()
  },
  {
    id: 'ordered',
    label: 'Ordered list',
    run: (ed) => ed.chain().focus().toggleOrderedList().run()
  },
  {
    id: 'code',
    label: 'Code block',
    run: (ed) => ed.chain().focus().toggleCodeBlock().run()
  },
  {
    id: 'quote',
    label: 'Quote',
    run: (ed) => ed.chain().focus().toggleBlockquote().run()
  },
  {
    id: 'divider',
    label: 'Divider',
    run: (ed) => ed.chain().focus().setHorizontalRule().run()
  },
  {
    id: 'link',
    label: 'Link',
    run: (ed) => ed.chain().focus().insertContent('[link](https://)').run()
  }
];

function filterItems<T extends { label: string }>(items: T[], q: string): T[] {
  if (!q) return items;
  const needle = q.toLowerCase();
  return items.filter((i) => i.label.toLowerCase().includes(needle));
}

export function WysiwygEditor({
  value,
  onChange,
  onSave,
  saveState,
  placeholder,
  readonly = false,
  className,
  dataTestId,
  hideInlineSave = false,
  showFormattingToolbar = false,
  flush = false
}: WysiwygEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [toastDismissed, setToastDismissed] = useState(false);
  const [menu, setMenu] = useState<MenuState | null>(null);
  // Bumped on every editor selectionUpdate / docUpdate so the formatting
  // toolbar re-evaluates `editor.isActive(...)` for the active-state styling
  // on each button.
  const [, setActiveTick] = useState(0);
  const menuRef = useRef<MenuState | null>(null);
  menuRef.current = menu;
  const editorRef = useRef<Editor | null>(null);
  const tid = (suffix: string) =>
    dataTestId ? `${dataTestId}-${suffix}` : `wysiwyg-${suffix}`;

  const items = useMemo(() => {
    if (!menu) return [] as SlashCommand[];
    return filterItems(SLASH_COMMANDS, menu.query);
  }, [menu]);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const openMenuAtCursor = () => {
    // Schedule to the next tick so the trigger character has been inserted
    // into the doc — we anchor *behind* the cursor so subsequent typing
    // extends the query without moving the anchor.
    setTimeout(() => {
      const ed = editorRef.current;
      if (!ed) return;
      const pos = ed.state.selection.from;
      const coords = ed.view.coordsAtPos(pos);
      setMenu({
        anchorPos: Math.max(0, pos - 1),
        x: coords.left,
        y: coords.bottom,
        query: '',
        index: 0
      });
    }, 0);
  };

  const selectItem = (idx: number) => {
    const m = menuRef.current;
    const ed = editorRef.current;
    const list = itemsRef.current;
    if (!m || !ed || list.length === 0) return;
    const item = list[idx];
    if (!item) return;
    const from = m.anchorPos;
    const to = ed.state.selection.from;
    // Remove the trigger + filter text the user typed.
    ed.chain().focus().deleteRange({ from, to }).run();
    const cmd = SLASH_COMMANDS.find((c) => c.id === item.id);
    cmd?.run(ed);
    setMenu(null);
  };

  const editor = useEditor(
    {
      extensions: [
        StarterKit,
        Markdown.configure({ html: false, tightLists: true, breaks: false })
      ],
      content: value,
      editable: !readonly,
      editorProps: {
        attributes: {
          class: 'pn-wysiwyg-content',
          'data-testid': tid('content'),
          'aria-label': placeholder ?? 'Document body'
        },
        handleKeyDown: (view, e) => {
          const m = menuRef.current;
          if (m) {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              const len = itemsRef.current.length || 1;
              setMenu((prev) =>
                prev ? { ...prev, index: (prev.index + 1) % len } : prev
              );
              return true;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              const len = itemsRef.current.length || 1;
              setMenu((prev) =>
                prev ? { ...prev, index: (prev.index - 1 + len) % len } : prev
              );
              return true;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              selectItem(m.index);
              return true;
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              setMenu(null);
              return true;
            }
            // Other keys (letters, backspace) fall through; the doc-update
            // listener below will refresh the running query.
            return false;
          }
          if (e.key === '/') {
            const $from = view.state.selection.$from;
            const atStartOfEmptyPara =
              $from.parentOffset === 0 &&
              $from.parent.type.name === 'paragraph' &&
              $from.parent.content.size === 0;
            if (atStartOfEmptyPara) openMenuAtCursor();
          }
          return false;
        }
      },
      onUpdate: ({ editor: ed }) => {
        onChange(getMarkdown(ed));
      }
    },
    []
  );

  // Reset toast-dismissed state whenever a new failure transition occurs.
  useEffect(() => {
    if (saveState === 'failed') setToastDismissed(false);
  }, [saveState]);

  // Sync external `value` into the editor when it changes from the outside
  // (e.g., parent reset after a successful save). Avoid feedback loops by
  // only writing when the rendered markdown actually differs.
  useEffect(() => {
    if (!editor) return;
    const current = getMarkdown(editor);
    if (current !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readonly);
  }, [editor, readonly]);

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Keep the running popover query in sync with the editor doc. Closes the
  // menu if the user deleted the trigger character or moved the selection
  // before the anchor.
  useEffect(() => {
    if (!editor) return;
    const refresh = () => {
      const m = menuRef.current;
      if (!m) return;
      const docSize = editor.state.doc.content.size;
      const from = editor.state.selection.from;
      if (m.anchorPos >= docSize || from < m.anchorPos + 1) {
        setMenu(null);
        return;
      }
      let text = '';
      try {
        text = editor.state.doc.textBetween(m.anchorPos, from, '\n', '\n');
      } catch {
        setMenu(null);
        return;
      }
      if (!text.startsWith('/')) {
        setMenu(null);
        return;
      }
      const q = text.slice(1);
      setMenu((prev) =>
        prev && prev.query !== q ? { ...prev, query: q, index: 0 } : prev
      );
    };
    editor.on('selectionUpdate', refresh);
    editor.on('update', refresh);
    return () => {
      editor.off('selectionUpdate', refresh);
      editor.off('update', refresh);
    };
  }, [editor]);

  // Cmd/Ctrl+S → onSave. Scoped to the wrapper so the shortcut only fires
  // when the editor (or its SAVE button) is focused.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        // Page-level Cmd+S handlers (e.g. on WorkItemDetail / DocumentEditor)
        // listen on the document. Stop here so they don't also fire onSave —
        // a double-fire causes redundant network round-trips and can trigger
        // a spurious LWW conflict.
        e.stopPropagation();
        if (!readonly) onSave();
      }
    };
    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
  }, [onSave, readonly]);

  // Re-render the toolbar's active-state styling whenever the selection or
  // doc changes. Only subscribes when the toolbar is shown — keeps the
  // common (no-toolbar) path cheap.
  useEffect(() => {
    if (!editor || !showFormattingToolbar) return;
    const bump = () => setActiveTick((t) => t + 1);
    editor.on('selectionUpdate', bump);
    editor.on('update', bump);
    return () => {
      editor.off('selectionUpdate', bump);
      editor.off('update', bump);
    };
  }, [editor, showFormattingToolbar]);

  const saving = saveState === 'saving';
  const wrapperCls = [
    'pn-wysiwyg',
    flush ? 'pn-wysiwyg-flush' : '',
    className
  ]
    .filter(Boolean)
    .join(' ');
  const showToolbar = !readonly && (showFormattingToolbar || !hideInlineSave);

  const fmt = (run: (ed: Editor) => void, active = false, label: string, glyph: ReactNode) => {
    return (
      <button
        type="button"
        className={`pn-wysiwyg-fmt-btn${active ? ' is-active' : ''}`}
        data-testid={tid(`fmt-${label.toLowerCase().replace(/\s+/g, '-')}`)}
        data-active={active ? 'true' : 'false'}
        aria-label={label}
        aria-pressed={active}
        title={label}
        disabled={readonly || !editor}
        onMouseDown={(e) => {
          // Prevent the editor losing focus before the command runs.
          e.preventDefault();
        }}
        onClick={() => {
          if (!editor) return;
          run(editor);
        }}
      >
        {glyph}
      </button>
    );
  };

  return (
    <div
      ref={containerRef}
      className={wrapperCls}
      data-testid={tid('root')}
      data-save-state={saveState}
    >
      {saveState === 'conflict' ? (
        <div
          className="pn-wysiwyg-conflict"
          role="alert"
          data-testid={tid('conflict-banner')}
        >
          {CONFLICT_COPY}
        </div>
      ) : null}

      {showToolbar ? (
        <div className="pn-wysiwyg-toolbar" data-testid={tid('toolbar')}>
          {showFormattingToolbar ? (
            <div className="pn-wysiwyg-fmt-group" role="toolbar" aria-label="Text formatting">
              {fmt(
                (ed) => ed.chain().focus().toggleBold().run(),
                !!editor?.isActive('bold'),
                'Bold',
                <strong>B</strong>
              )}
              {fmt(
                (ed) => ed.chain().focus().toggleItalic().run(),
                !!editor?.isActive('italic'),
                'Italic',
                <em>I</em>
              )}
              <span className="pn-wysiwyg-fmt-sep" aria-hidden />
              {fmt(
                (ed) => ed.chain().focus().toggleHeading({ level: 1 }).run(),
                !!editor?.isActive('heading', { level: 1 }),
                'Heading 1',
                'H1'
              )}
              {fmt(
                (ed) => ed.chain().focus().toggleHeading({ level: 2 }).run(),
                !!editor?.isActive('heading', { level: 2 }),
                'Heading 2',
                'H2'
              )}
              {fmt(
                (ed) => ed.chain().focus().toggleHeading({ level: 3 }).run(),
                !!editor?.isActive('heading', { level: 3 }),
                'Heading 3',
                'H3'
              )}
              <span className="pn-wysiwyg-fmt-sep" aria-hidden />
              {fmt(
                (ed) => ed.chain().focus().toggleBulletList().run(),
                !!editor?.isActive('bulletList'),
                'Bullet list',
                '•'
              )}
              {fmt(
                (ed) => ed.chain().focus().toggleOrderedList().run(),
                !!editor?.isActive('orderedList'),
                'Ordered list',
                '1.'
              )}
              <span className="pn-wysiwyg-fmt-sep" aria-hidden />
              {fmt(
                (ed) => ed.chain().focus().toggleBlockquote().run(),
                !!editor?.isActive('blockquote'),
                'Quote',
                '❝'
              )}
              {fmt(
                (ed) => ed.chain().focus().toggleCodeBlock().run(),
                !!editor?.isActive('codeBlock'),
                'Code block',
                '< >'
              )}
              {fmt(
                (ed) => ed.chain().focus().insertContent('[link](https://)').run(),
                false,
                'Link',
                '🔗'
              )}
              {fmt(
                (ed) => ed.chain().focus().setHorizontalRule().run(),
                false,
                'Divider',
                '—'
              )}
            </div>
          ) : null}
          {hideInlineSave ? null : (
            <button
              type="button"
              className={`pn-btn pn-btn-primary pn-wysiwyg-save pn-wysiwyg-save-${saveState}`}
              data-testid={tid('save-btn')}
              data-save-state={saveState}
              aria-label={saveButtonLabel(saveState)}
              onClick={onSave}
              disabled={readonly || saving}
            >
              {saveButtonLabel(saveState)}
            </button>
          )}
        </div>
      ) : null}

      <EditorContent editor={editor} />

      {menu && items.length > 0 ? (
        <div
          className="pn-wysiwyg-menu"
          role="listbox"
          data-testid={tid('slash-menu')}
          style={{ position: 'fixed', left: menu.x, top: menu.y + 4 }}
        >
          {items.map((it, i) => (
            <button
              key={it.id}
              type="button"
              role="option"
              aria-selected={i === menu.index}
              data-active={i === menu.index ? 'true' : 'false'}
              data-testid={tid(`slash-item-${it.id}`)}
              className={`pn-wysiwyg-menu-item${i === menu.index ? ' is-active' : ''}`}
              onMouseDown={(e) => {
                // Prevent the editor losing focus before we run the command.
                e.preventDefault();
                selectItem(i);
              }}
              onMouseEnter={() =>
                setMenu((prev) => (prev ? { ...prev, index: i } : prev))
              }
            >
              {it.label}
            </button>
          ))}
        </div>
      ) : null}

      {saveState === 'failed' && !toastDismissed ? (
        <Toast
          title="Couldn't save"
          onDismiss={() => setToastDismissed(true)}
          className="pn-wysiwyg-toast"
        >
          Your changes are still here. Try saving again.
        </Toast>
      ) : null}
    </div>
  );
}
